<template>
  <b-overlay v-if="!cardClosed" variant="white" :show="showLoading" spinner-variant="primary" blur="0" opacity=".75"
    rounded="sm">
    <b-card ref="bCard" v-bind="cardAttrs" no-body :aria-expanded="content_visible ? 'true' : 'false'"
      :aria-controls="parentID" :style="cardStyles" v-on="$listeners">
      <div v-if="showHeader" class="card-header" @click="handleHeaderClick">
        <!-- Title & SubTitle -->
        <div>
          <b-card-title>
            {{ $attrs.title }}
            <b-badge v-if="notifCount" variant="light-danger">
              {{ notifCount }}
            </b-badge>
            <span v-if="competencyScores">
              - 
              <small>
                <span class="mr-50" v-for="(score, key) in competencyScores" :key="key">
                  <b-badge :variant="'light-' + getBadgeVariant(score)">
                    <strong>{{ key }}:</strong> {{ score.toFixed(2) }}
                  </b-badge>
                </span>
              </small>
            </span>
          </b-card-title>
          <b-card-sub-title v-if="$attrs['sub-title']">
            {{ $attrs['sub-title'] }}
          </b-card-sub-title>
        </div>
        <span class="d-flex align-items-center prevent-collapse tooltip-wrapper" v-b-tooltip.hover.top="tooltipText">
          <!-- Card Actions -->
          <b-card-actions-container v-if="showActions" :available-actions="availableActions"
            :is-collapsed="!content_visible" @collapse="triggerCollapse" @refresh="triggerRefresh" @close="triggerClose"
            @click.stop />
        </span>
      </div>

      <!-- Collapsible Content -->
      <b-collapse :id="parentID" v-model="content_visible" class="card-content">
        <!-- Handle no-body -->
        <slot v-if="$attrs['no-body'] !== undefined" />
        <b-card-body v-else>
          <slot />
        </b-card-body>
      </b-collapse>
    </b-card>
  </b-overlay>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardSubTitle,
  BCardBody,
  BCollapse,
  BOverlay,
  BBadge,
  BButton,
  VBModal,
  VBTooltip
} from 'bootstrap-vue';
import BCardActionsContainer from './BCardActionsContainer.vue';
import Ripple from 'vue-ripple-directive';

export default {
  components: {
    BCardActionsContainer,
    BCard,
    BCardTitle,
    BCardSubTitle,
    BCardBody,
    BCollapse,
    BOverlay,
    BBadge,
    BButton,
  },
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  inheritAttrs: false,
  props: {
    notifCount: {
      type: Number,
      required: false,
      default: 0,
    },
    competencyScores: {
      type: Object,
      required: false,
    },
    collapsed: {
      type: Boolean,
      default: true,
    },
    noActions: {
      type: Boolean,
      default: false,
    },
    actionCollapse: {
      type: Boolean,
      default: false,
    },
    actionRefresh: {
      type: Boolean,
      default: false,
    },
    actionClose: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      parentID: String(Math.floor(Math.random() * 10) + 1),
      content_visible: !this.collapsed,
      showLoading: false,
      cardClosed: false,
      cardStyles: {},
    };
  },
  computed: {
    cardAttrs() {
      const cardAttrs = { ...this.$attrs };
      delete cardAttrs.title;
      delete cardAttrs['sub-title'];
      return cardAttrs;
    },
    showHeader() {
      return this.$attrs.title || this.$attrs['sub-title'] || !this.noActions;
    },
    showActions() {
      return !this.noActions;
    },
    availableActions() {
      const actions = [];
      if (this.actionCollapse || this.actionRefresh || this.actionClose) {
        if (this.actionCollapse) actions.push('collapse');
        if (this.actionRefresh) actions.push('refresh');
        if (this.actionClose) actions.push('close');
      } else {
        actions.push('collapse', 'refresh', 'close');
      }
      return actions;
    },
    tooltipText() {
      return ''
    },
  },
  methods: {
    handleHeaderClick(event) {
      if (!event.target.closest('.prevent-collapse')) {
        this.triggerCollapse();
      }
    },
    removeCard() {
      this.$set(this.cardStyles, 'maxHeight', `${this.$refs.bCard.clientHeight}px`);
      setTimeout(() => {
        this.$set(this.cardStyles, 'maxHeight', '0px');
        this.$set(this.cardStyles, 'overflow', 'hidden');
      }, 10);
    },
    triggerCollapse() {
      this.content_visible = !this.content_visible;
      this.$emit('collapse');
    },
    triggerRefresh() {
      this.showLoading = true;
      this.$emit('refresh');
      setTimeout(() => {
        this.showLoading = false;
      }, 1000);
    },
    triggerClose() {
      this.removeCard();
      this.$emit('close');
    },
    getBadgeVariant(score) {
      if (score < 1) return 'danger';
      if (score < 2) return 'warning';
      return 'success';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.card {
  ::v-deep .card-header {
    .heading-elements {
      position: static;
      cursor: inherit;

      .list-inline {
        display: block;

        li {
          a {
            padding: 0;
          }

          &:not(:last-child) {
            margin-right: 1rem;
          }
        }
      }
    }
  }
}

.dark-layout {
  .b-overlay-wrap ::v-deep .b-overlay {
    .bg-white {
      background-color: $theme-dark-body-bg !important;
    }
  }
}
</style>
